import { Linking, Pressable, Text, View } from "react-native";
import type { StyleProp, ViewStyle } from "react-native";
import { ExternalLink } from "lucide-react-native";
import type { EvidenceSource } from "@src/packages/content";
import { EMBER } from "../../onboarding/theme";
import EvidenceLevelChip, { EVIDENCE_LEVELS } from "./EvidenceLevelChip";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
import { semanticColors } from "@louise/theme/tokens";

type Props = {
  sources: EvidenceSource[];
  onEvidenceLevelPress: (level: string) => void;
  style?: StyleProp<ViewStyle>;
};

function formatSourceLabel(source: EvidenceSource) {
  return `${source.title}${source.journal ? ` - ${source.journal}` : ""}${
    source.year ? ` (${source.year})` : ""
  }`;
}

export default function EvidenceSourceRows({
  sources,
  onEvidenceLevelPress,
  style,
}: Props) {
  const capturePress = usePressCapture("EvidenceSourceRows");

  if (!sources.length) return null;

  return (
    <View style={style}>
      {sources.map((source, i) => {
        const hasUrl = !!source.url;
        const rowStyle = {
          gap: 9,
          paddingTop: i === 0 ? 0 : 12,
          paddingBottom: i === sources.length - 1 ? 0 : 12,
          borderTopWidth: i === 0 ? 0 : 1,
          borderTopColor: semanticColors.border.subtle,
        } as const;
        const inner = (
          <>
            {source.evidenceLevel && EVIDENCE_LEVELS[source.evidenceLevel] ? (
              <EvidenceLevelChip
                level={source.evidenceLevel}
                onPress={() => onEvidenceLevelPress(source.evidenceLevel!)}
              />
            ) : null}
            <View
              style={{
                flexDirection: "row",
                alignItems: "flex-start",
                gap: 6,
              }}
            >
              <Text
                className="font-dm-sans"
                style={{
                  flex: 1,
                  fontSize: 13,
                  color: EMBER.colors.textLight,
                  lineHeight: 18,
                }}
              >
                {formatSourceLabel(source)}
              </Text>
              {hasUrl ? (
                <ExternalLink
                  size={12}
                  color={EMBER.colors.textFaint}
                  strokeWidth={1.75}
                  style={{ marginTop: 4 }}
                />
              ) : null}
            </View>
          </>
        );

        if (!hasUrl) {
          return (
            <View
              key={`${source.title}-${source.year ?? "source"}-${i}`}
              style={rowStyle}
            >
              {inner}
            </View>
          );
        }

        return (
          <Pressable
            key={`${source.title}-${source.year ?? "source"}-${i}`}
            onPress={() => {
              // Citation text is authored reference material, not patient data.
              capturePress(
                pressLabel(formatSourceLabel(source)),
                "evidence_source_link",
              );
              Linking.openURL(source.url!).catch(() => {});
            }}
            style={({ pressed }) => [
              rowStyle,
              pressed ? { opacity: 0.6 } : null,
            ]}
          >
            {inner}
          </Pressable>
        );
      })}
    </View>
  );
}
